import { useCallback, useEffect, useState } from 'react';
import './SelectHL7Modal.scss';
import Modal from '../../../BaseComponents/Modal';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router';
import { InuviFileInfo } from '../../../../types/apiGeneratedTypes';
import axiosInstance from '../../../../clients/axiosClient';
import { API_V1_PREFIX, MEMBERS_API_PREFIX } from '../../../../utils/constants';
import { parseHL7 } from '../../../../utils/hl7Parser';
export interface SelectHL7ModalTypes {
  appointment: {
    key: any;
    label: any;
  };
  biomarkers: {
    id: any;
    name: {
      key: any;
      value: any;
    };
    summary: any;
    value: any;
    measurement_unit: any;
    biomarker_result_id?: any;
  }[];
}

interface SelectHL7ModalProps {
  onClose: () => void;
  onSelect: (res: any) => void;
}
interface HL7File extends InuviFileInfo {
  content?: string;
}

const SelectHL7Modal = ({ onClose, onSelect }: SelectHL7ModalProps) => {
  const [files, setFiles] = useState<HL7File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [parsedHL7, setParsedHL7] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const location = useLocation();

  const fetchFiles = useCallback(async () => {
    try {
      setIsLoading(true);
      const memberId = location.pathname.split('/')[2];
      const { data } = await axiosInstance.get(
        `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${memberId}/inuvi-files`
      );
      setFiles(data.files);
    } catch (error) {
      toast.error('Error loading HL7 files');
      console.error('Error fetching files:', error);
    } finally {
      setIsLoading(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleParseFile = async (file: HL7File) => {
    try {
      // Fetch file content
      const response = await fetch(file.download_url, {
        method: 'GET',
        headers: {
          Accept: 'text/plain'
        },
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const content = await response.text();

      // Parse HL7 content
      const parsed = parseHL7(content);
      return parsed;
    } catch (error) {
      toast.error('Error loading file content');
      console.error('Error loading file content:', error);
      return false;
    }
  };
  const handleViewFile = async (file: HL7File) => {
    setIsLoadingContent(true);
    setShowViewModal(true);

    // Fetch file content
    const res = await handleParseFile(file);
    if (res) {
      setParsedHL7(res);
    }

    setIsLoadingContent(false);
  };
  const handleSelectFile = async (file: HL7File) => {
    const res = await handleParseFile(file);
    if (res) {
      onSelect(res);
      onClose();
    }
  };

  return (
    <Modal isOpen width='800px' onClose={onClose}>
      <div className='select-container'>
        <div className='files-table'>
          {isLoading ? (
            <div style={{ textAlign: 'center' }}>Loading content...</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.id}>
                    <td>{file.file_key}</td>
                    <td className='actions'>
                      <button
                        className='action-button'
                        onClick={(e) => {
                          e.preventDefault();
                          handleViewFile(file);
                        }}
                        title='View File'
                      >
                        Preview
                      </button>
                      <button
                        className='action-button'
                        title='Download File'
                        onClick={(e) => {
                          e.preventDefault();
                          handleSelectFile(file);
                        }}
                      >
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        width='970px'
      >
        <div className='hl7-viewer-modal'>
          <h2>HL7 File Content</h2>
          {isLoadingContent ? (
            <div>Loading content...</div>
          ) : parsedHL7 ? (
            <>
              <div className='member-info'>
                <h3>Member Information</h3>
                <div className='info-grid'>
                  <div className='info-item'>
                    <span className='label'>ID:</span>
                    <span className='value'>{parsedHL7.memberInfo.id}</span>
                  </div>
                  <div className='info-item'>
                    <span className='label'>Name:</span>
                    <span className='value'>{parsedHL7.memberInfo.name}</span>
                  </div>
                  <div className='info-item'>
                    <span className='label'>Date of Birth:</span>
                    <span className='value'>
                      {parsedHL7.memberInfo.dateOfBirth}
                    </span>
                  </div>
                  <div className='info-item'>
                    <span className='label'>Gender:</span>
                    <span className='value'>{parsedHL7.memberInfo.gender}</span>
                  </div>
                </div>
              </div>

              <div className='test-results'>
                <h3>Test Results</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Test Name</th>
                      <th>Value</th>
                      <th>Unit</th>
                      <th>Reference Range</th>
                      <th>Status</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedHL7.testResults.map((result: any, index: number) => (
                      <tr key={index}>
                        <td>{result.testName}</td>
                        <td>{result.value}</td>
                        <td>{result.unit}</td>
                        <td>{result.referenceRange}</td>
                        <td>{result.status}</td>
                        <td>
                          {result.notes.map(
                            (note: string, noteIndex: number) => (
                              <div key={noteIndex} className='note-item'>
                                {note}
                              </div>
                            )
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div>No content available</div>
          )}
        </div>
      </Modal>
    </Modal>
  );
};

export default SelectHL7Modal;
